import React, {useEffect, useState} from 'react';
import {
  Autocomplete,
  Button,
  Checkbox,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField
} from '@mui/material';
import {Delete, History, Info, Person} from '@mui/icons-material';
import Lottie from 'lottie-react';
import loadingAnimation from '../../../../assets/lottie/loading.json';
import api from '../../../../api/axios';
import {useCurrency} from '../../../../hooks/useCurrency';
import {moneyFromMinor} from '../../../utils/UnitConversion.utils';
import {toast} from 'react-toastify';
import BuyOrderInfoModal from '../../profile/buyorder/BuyOrderInfoModal';
import SystemBuyOrderDeleteModal from './SystemBuyOrderDeleteModal';

export type BuyOrderStatus = 'PENDING' | 'PAUSED' | 'COMPLETED' | 'CANCELLED';

export interface BuyOrderSystem {
  id: number;
  itemName: string;
  imageLink: string;
  quantity: number;
  completedQuantity: number;
  priceMinor: number;
  status: BuyOrderStatus;
  creationDate: string;
  steamAccountId: number;
  userId: number;
}

const SystemBuyOrder = () => {
  const {convertFromUSD, currencySymbol, formatAmountWithoutCurrency} = useCurrency();

  const [orders, setOrders] = useState<BuyOrderSystem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const [searchValue, setSearchValue] = useState<string | null>(null);
  const [userIdFilter, setUserIdFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<BuyOrderStatus | 'ALL'>('ALL');

  const [inputValue, setInputValue] = useState('');
  const [searchOptions, setSearchOptions] = useState<string[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const [infoOpen, setInfoOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<BuyOrderSystem | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState('creationDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Helper to check if an order is selectable
  const isSelectable = (order: BuyOrderSystem) => order.status !== 'CANCELLED' && order.status !== 'COMPLETED';

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/system/buy-orders`, {
        params: {
          sort: `${sortBy},${sortDirection}`,
          page,
          size: rowsPerPage,
          itemName: searchValue || undefined,
          userId: userIdFilter || undefined,
          status: statusFilter === 'ALL' ? undefined : statusFilter
        }
      });
      setOrders(response.data.content);
      setTotalCount(response.data.totalElements);
      setSelectedIds([]);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [page, rowsPerPage, sortBy, sortDirection, searchValue, statusFilter]);

  const handleBatchDelete = async (ids: number[]) => {
    try {
      await toast.promise(
          api.post('/system/buy-orders/batch-delete', ids),
          {
            pending: 'Deleting selected orders...',
            success: 'Orders deleted successfully',
            error: 'Batch delete failed',
          }
      );
      fetchOrders();
    } catch (error) {
      console.error('Batch delete error:', error);
    }
  };

  const toggleSelectAll = () => {
    const selectableOrders = orders.filter(isSelectable);
    if (selectedIds.length === selectableOrders.length && selectableOrders.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(selectableOrders.map(o => o.id));
    }
  };

  const toggleSelect = (order: BuyOrderSystem) => {
    if (!isSelectable(order)) return; // Prevent selection of completed/cancelled
    setSelectedIds(prev => prev.includes(order.id) ? prev.filter(i => i !== order.id) : [...prev, order.id]);
  };

  const fetchSearchSuggestions = async (query: string) => {
    if (query.length < 2) {
      setSearchOptions([]);
      return;
    }
    setSearchLoading(true);
    try {
      const response = await api.get<string[]>(`/buy-orders/search-item`, {params: {itemName: query}});
      setSearchOptions(response.data);
    } catch (e) {
      console.error(e);
    } finally {
      setSearchLoading(false);
    }
  };

  const getStatusStyle = (status: BuyOrderStatus) => {
    switch (status) {
      case 'PENDING': return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
      case 'PAUSED': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      case 'COMPLETED': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'CANCELLED': return 'bg-red-500/10 text-red-500 border-red-500/20';
      default: return 'bg-neutral-500/10 text-neutral-500 border-neutral-500/20';
    }
  };

  return (
      <div className="flex flex-col md:p-4 p-2 bg-white dark:bg-neutral-900/40 border border-gray-100 dark:border-white/5 min-h-[88vh]">
        <div className="flex flex-col xl:flex-row justify-between items-center gap-4 mb-4 shrink-0">
          <div className="flex items-center gap-2 self-start xl:self-center">
            <History className="text-dark-primary"/>
            <h2 className="text-lg font-bold text-gray-800 dark:text-white uppercase tracking-tighter">
              System Buy Orders
            </h2>
          </div>

          <div className="flex flex-wrap flex-row w-full md:max-w-5xl gap-3 justify-between md:justify-end items-center">
            <div className="flex bg-gray-50 dark:bg-neutral-800/50 p-1 rounded-none border border-black/5 dark:border-white/5 w-full md:w-auto order-3 md:order-1 justify-center md:justify-start">
              {['ALL', 'PENDING', 'PAUSED', 'COMPLETED', 'CANCELLED'].map((s) => (
                  <button
                      key={s}
                      onClick={() => { setStatusFilter(s as any); setPage(0); }}
                      className={`flex-1 md:flex-none px-3 py-1.5 text-[10px] font-black uppercase tracking-tighter transition-all ${
                          statusFilter === s ? 'bg-dark-primary text-white shadow-lg' : 'text-neutral-500 hover:text-neutral-300'
                      }`}
                  >
                    {s}
                  </button>
              ))}
            </div>

            <div className="flex flex-1 md:flex-none gap-2 items-center w-full md:w-auto order-1 md:order-2">
              <TextField
                  size="small"
                  placeholder="User ID..."
                  value={userIdFilter}
                  onChange={(e) => { setUserIdFilter(e.target.value); setPage(0); }}
                  onKeyDown={(e) => e.key === 'Enter' && fetchOrders()}
                  className="bg-gray-50 dark:bg-neutral-800/50 w-24"
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {borderColor: 'rgba(0,0,0,0.05)', borderRadius: 0},
                    '& .MuiInputBase-input': {fontSize: '11px', color: 'white'}
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Person sx={{fontSize: 16, color: '#555'}}/></InputAdornment>
                  }}
              />

              <Autocomplete
                  fullWidth
                  size="small"
                  options={searchOptions}
                  loading={searchLoading}
                  value={searchValue}
                  inputValue={inputValue}
                  onInputChange={(_, val) => { setInputValue(val); fetchSearchSuggestions(val); }}
                  onChange={(_, val) => { setSearchValue(val); setPage(0); }}
                  className="flex-1 md:w-64"
                  renderInput={(params) => (
                      <TextField
                          {...params}
                          placeholder="Item Name..."
                          className="bg-gray-50 dark:bg-neutral-800/50"
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {borderColor: 'rgba(0,0,0,0.05)', borderRadius: 0},
                            '& .MuiInputBase-input': {fontSize: '11px', color: 'white'}
                          }}
                      />
                  )}
              />

              {selectedIds.length > 0 && (
                  <Button
                      variant="contained"
                      color="error"
                      onClick={() => handleBatchDelete(selectedIds)}
                      className="h-[36px] px-4 text-xs font-bold animate-pulse"
                      sx={{borderRadius: 0}}
                      startIcon={<Delete/>}
                  >
                    Cancel ({selectedIds.length})
                  </Button>
              )}
            </div>
          </div>
        </div>

        <Divider className="!border-gray-100 dark:!border-white/5"/>

        {loading && orders.length === 0 ? (
            <div className="flex-1 flex justify-center items-center h-[400px]">
              <Lottie animationData={loadingAnimation} loop className="w-64"/>
            </div>
        ) : (
            <TableContainer component={Paper} className="bg-transparent shadow-none border-none mt-4 overflow-hidden" sx={{borderRadius: 0}}>
              <Table size="small" className="border-separate border-spacing-y-2">
                <TableHead>
                  <TableRow className="bg-gray-50/50 dark:bg-white/[0.02]">
                    <TableCell padding="checkbox" sx={{border: 'none'}}>
                      <Checkbox
                          size="small"
                          // Logic for header checkbox: only consider selectable ones
                          indeterminate={selectedIds.length > 0 && selectedIds.length < orders.filter(isSelectable).length}
                          checked={orders.filter(isSelectable).length > 0 && selectedIds.length === orders.filter(isSelectable).length}
                          onChange={toggleSelectAll}
                          sx={{color: '#444', '&.Mui-checked': {color: '#3b82f6'}}}
                      />
                    </TableCell>
                    <TableCell className="text-[10px] font-black text-gray-400 uppercase tracking-widest" sx={{border: 'none'}}>Item</TableCell>
                    <TableCell className="text-[10px] font-black text-gray-400 uppercase tracking-widest" sx={{border: 'none'}}>User</TableCell>
                    <TableCell className="text-[10px] font-black text-gray-400 uppercase tracking-widest" sx={{border: 'none'}}>Status</TableCell>
                    <TableCell align="center" className="text-[10px] font-black text-gray-400 uppercase tracking-widest" sx={{border: 'none'}}>Qty</TableCell>
                    <TableCell className="text-[10px] font-black text-gray-400 uppercase tracking-widest" sx={{border: 'none'}}>Price</TableCell>
                    <TableCell className="text-[10px] font-black text-gray-400 uppercase tracking-widest" sx={{border: 'none'}}>Date</TableCell>
                    <TableCell align="right" className="text-[10px] font-black text-gray-400 uppercase tracking-widest" sx={{border: 'none'}}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((order) => {
                    const selectable = isSelectable(order);
                    return (
                        <TableRow
                            key={order.id}
                            selected={selectedIds.includes(order.id)}
                            className={`group bg-gray-50/30 dark:bg-neutral-900/20 transition-all ${selectable ? 'hover:bg-white/[0.03] cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}
                            onClick={() => toggleSelect(order)}
                        >
                          <TableCell padding="checkbox" sx={{border: 'none'}}>
                            <Checkbox
                                size="small"
                                disabled={!selectable}
                                checked={selectedIds.includes(order.id)}
                                sx={{color: '#444', '&.Mui-checked': {color: '#3b82f6'}}}
                            />
                          </TableCell>
                          <TableCell sx={{border: 'none'}}>
                            <div className="flex items-center gap-3">
                              <img src={order.imageLink} referrerPolicy="no-referrer" className="w-8 h-8 object-contain" alt=""/>
                              <div className="flex flex-col">
                                <span className="font-bold text-gray-200 text-xs">{order.itemName}</span>
                                <span className="text-[10px] text-gray-500 font-mono italic">#{order.id}</span>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell sx={{border: 'none'}}>
                            <div className="flex flex-col">
                              <span className="text-xs text-neutral-300 font-bold">UID: {order.userId}</span>
                              <span className="text-[10px] text-neutral-500">Acc: {order.steamAccountId}</span>
                            </div>
                          </TableCell>
                          <TableCell sx={{border: 'none'}}>
                            <span className={`text-[9px] px-2 py-0.5 font-black uppercase border ${getStatusStyle(order.status)}`}>
                              {order.status}
                            </span>
                          </TableCell>
                          <TableCell align="center" sx={{border: 'none'}}>
                            <span className="text-[10px] font-mono font-bold text-neutral-400">
                              {order.completedQuantity}/{order.quantity}
                            </span>
                          </TableCell>
                          <TableCell sx={{border: 'none'}}>
                            <span className="font-mono font-bold text-emerald-500 text-xs">
                              {currencySymbol}{formatAmountWithoutCurrency(convertFromUSD(moneyFromMinor(order.priceMinor)))}
                            </span>
                          </TableCell>
                          <TableCell sx={{border: 'none'}} className="text-[10px] text-gray-500">
                            {new Date(order.creationDate).toLocaleDateString('de-DE')}
                          </TableCell>
                          <TableCell align="right" sx={{border: 'none'}}>
                            <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                              <IconButton size="small" onClick={() => { setSelectedOrder(order); setInfoOpen(true); }}>
                                <Info sx={{fontSize: 16}} className="text-dark-primary"/>
                              </IconButton>
                              {selectable && (
                                  <IconButton size="small" onClick={() => { setSelectedOrder(order); setDeleteOpen(true); }}>
                                    <Delete sx={{fontSize: 16}} className="text-red-500"/>
                                  </IconButton>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
        )}

        <div className="flex justify-between items-center pt-4 border-t border-white/5 mt-auto shrink-0">
          <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 20, 50]}
              sx={{color: '#777', '.MuiTablePagination-selectIcon': {color: '#777'}}}
          />
          <div className="text-[10px] text-gray-500 uppercase font-black">Admin View: {totalCount} Total</div>
        </div>

        <BuyOrderInfoModal open={infoOpen} order={selectedOrder as any} onClose={() => setInfoOpen(false)} />
        <SystemBuyOrderDeleteModal open={deleteOpen} order={selectedOrder as any} onClose={() => setDeleteOpen(false)} onDeleted={fetchOrders} />
      </div>
  );
};

export default SystemBuyOrder;